type NativeBridge = {
  disableAppRefresh?: () => void;
  enableAppRefresh?: () => void;
  downloadBlob?: (url: string, fileName?: string) => void;
  pushUserDetails?: (...args: unknown[]) => unknown;
  pushUserDetailsV1?: (...args: unknown[]) => unknown;
  redirectToBrowser?: (url: string) => void;
  userLoggedOut?: (loggedOut: boolean) => void;
} & Record<string, unknown>;

type NativeBridgeWindow = Window & {
  Android?: NativeBridge;
  ReactNativeWebView?: NativeBridge;
};

export type NativeBridgeFunction = (...args: unknown[]) => unknown;

function readReactNativeBridgeFunction(
  bridge: NativeBridge | undefined,
  functionName: string
): NativeBridgeFunction | undefined {
  if (typeof bridge?.[functionName] !== 'function') return undefined;
  return (bridge[functionName] as NativeBridgeFunction).bind(bridge);
}

function readAndroidBridgeFunction(
  bridge: NativeBridge | undefined,
  functionName: string
): NativeBridgeFunction | undefined {
  if (typeof bridge?.[functionName] !== 'function') return undefined;

  // Call as `Android[name](...)`. bind/call/apply drops the injected Java
  // object and raises "Java exception was raised during method invocation".
  return (...args: unknown[]) => {
    if (typeof bridge[functionName] !== 'function') return undefined;
    return (bridge[functionName] as NativeBridgeFunction)(...args);
  };
}

/**
 * Selects one WebView function at its call site. React Native is preferred
 * only when it exposes that function; otherwise the legacy Android bridge is
 * retained as the fallback. Function names are intentionally not enumerated,
 * so React-Native-only methods can be added without changing this module.
 */
export function getNativeBridgeFunction(
  functionName: string
): NativeBridgeFunction | undefined {
  if (typeof window === 'undefined') return undefined;

  const bridgeWindow = window as NativeBridgeWindow;
  return (
    readReactNativeBridgeFunction(
      bridgeWindow.ReactNativeWebView,
      functionName
    ) ?? readAndroidBridgeFunction(bridgeWindow.Android, functionName)
  );
}
